import Phaser from 'phaser';
import {
  COPYRIGHT_NOTICE,
  DISCORD_URL,
  LEGAL_PAGES_LIVE,
  PRIVACY_URL,
  REDDIT_URL,
  SUPPORT_EMAIL,
  TERMS_URL,
} from '../../config/branding.js';
import { CHANGELOG, type Release } from '../../content/changelog.js';
import { APP_VERSION } from '../../content/version.js';
import { openExternal } from '../../platform/index.js';
import { mountPageHeader } from '../view/PageHeader.js';
import { attachBottomNav, bottomNavTopY, preloadNavAssets } from '../view/AppNav.js';
import { SectionTabs } from '../view/SectionTabs.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// Page background — same near-black navy as HomeScene so the screens
// feel like one product. This page is meant to read like a terminal
// readout, so we lean on FONT_MONO (JetBrains Mono) — distinct from
// the in-game body font (Kode Mono) — and ASCII-style separators.
const PAGE_BG_INT = 0x040713;

// Layout constants. Header band hosts the back button + a faint
// scanline divider; everything below it lives in a scrollable Container.
//
// IMPORTANT: do NOT compute constants from DESIGN_WIDTH/DESIGN_HEIGHT
// at module-load time — this scene is imported by PhaserGame, and
// the design constants live in PhaserGame, so reading them at module
// scope creates a circular-import temporal dead zone (the page just
// fails to load). Compute viewport-relative values inside methods.
const SIDE_PADDING = 36;
const VIEWPORT_BOTTOM_INSET = 24;

// Vertical rhythm. Sections breathe; bullets sit close.
const LINE_GAP_TIGHT = 6;
const LINE_GAP_LOOSE = 18;
const SECTION_GAP = 36;

// Text size scale. At 720 design width, real-device users found the
// body type tight without pinch-and-zoom. Bumping content fonts 1.5×
// is the cheapest fix — tweak this single constant to dial the page
// up or down without editing every text element. Base sizes below
// stay as the design intent; multiplication happens in `fs()`.
const TEXT_SCALE = 1.5;
function fs(base: number): string {
  return `${Math.round(base * TEXT_SCALE)}px`;
}


/**
 * Roadmap / release-notes screen. Reads like a tactical terminal
 * readout — wide BattleShark logo at the top, then `> SECTION` headers,
 * `[+]` / `[ ]` bullet markers, and a final `// END OF TRANSMISSION`
 * sign-off. Drag-to-scroll on touch; mouse wheel on desktop.
 */
export class AboutScene extends Phaser.Scene {
  private content!: Phaser.GameObjects.Container;
  private contentHeight = 0;
  // Set in create() from mountPageHeader's return; drives scroll
  // mask + drag-input gating below the static header.
  private contentTop = 0;
  private dragStart: { pointerY: number; contentY: number } | null = null;

  constructor() {
    super('About');
  }

  preload(): void {
    preloadNavAssets(this);
    // Wide HUD logo (logo2.png) — same asset GameScene's HUD uses, so
    // testers see the same brand mark in both places.
    this.load.image('aboutLogo', '/assets/logo2.png');
  }

  create(): void {
    this.drawBackground();
    const { contentTop } = mountPageHeader({
      scene: this,
      logoAssetKey: 'aboutLogo',
      showBack: true,
    });
    this.contentTop = contentTop;
    this.content = this.add.container(0, contentTop);
    this.buildContent();
    this.applyScrollMask();
    this.installScrollInput();
    attachBottomNav(this, 'settings');
  }

  // --- Background ---

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(PAGE_BG_INT, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  // --- Scrollable content ---

  private buildContent(): void {
    let y = 0;

    // Section nav butts directly against the logo (the logo lives in
    // scene root via mountPageHeader; content starts flush below it).
    y = this.addSectionNav(y);
    y += SECTION_GAP;

    y = this.addCurrentRelease(y);
    y += SECTION_GAP;

    y = this.addPlannedReleases(y);
    y += SECTION_GAP;

    y = this.addPastReleases(y);
    y += SECTION_GAP;

    y = this.addSignOff(y);
    y += SECTION_GAP;

    y = this.addLegalFooter(y);

    this.contentHeight = y;
  }

  // Shared 3-tab section nav across Settings / HowTo / About.
  private addSectionNav(y: number): number {
    const tabs = new SectionTabs(this, {
      x: 0,
      y,
      width: DESIGN_WIDTH,
      items: [
        { id: 'howto', label: 'HOW TO PLAY' },
        { id: 'settings', label: 'SETTINGS' },
        { id: 'about', label: 'INFO' },
      ],
      activeIndex: 2,
      onSelect: (id) => {
        // Intra-group navigation — see SettingsScene.addSectionNav
        // for the rationale. Plain scene.start() preserves the
        // history pointer at whatever opened the group.
        if (id === 'howto') this.scene.start('HowTo');
        else if (id === 'settings') this.scene.start('Settings');
      },
    });
    this.content.add(tabs.root);
    return y + tabs.height;
  }

  private addCurrentRelease(y: number): number {
    const current = CHANGELOG.find((r) => r.version === APP_VERSION);
    if (!current) return y;

    y = this.addCenteredLabel(y, 'CURRENT BUILD', COLORS.textMuted);
    y += LINE_GAP_TIGHT;

    // Big release headline: `vX.Y.Z — CODENAME`, with `versionDisplay`
    // overriding the default `v${version}` rendering when an entry
    // consolidates multiple patches under one banner (e.g. setting
    // `versionDisplay: 'v0.7 → v0.7.3'` when 0.7 + its patches collapse
    // into one recap row).
    const versionStr = current.versionDisplay ?? `v${current.version}`;
    const headline = this.add
      .text(DESIGN_WIDTH / 2, y, `${versionStr} · ${current.codename.toUpperCase()}`, {
        fontFamily: FONT_HEADER,
        fontSize: fs(28),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0);
    this.content.add(headline);
    y += headline.height + LINE_GAP_TIGHT;

    if (current.released) {
      const stamp = this.add
        .text(DESIGN_WIDTH / 2, y, `RELEASED ${current.released}`, {
          fontFamily: FONT_MONO,
          fontSize: fs(12),
          color: COLORS.textSecondary,
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0.5, 0);
      this.content.add(stamp);
      y += stamp.height + LINE_GAP_LOOSE;
    } else {
      y += LINE_GAP_LOOSE;
    }
    y = this.addSectionHeader(y, 'WHAT\'S NEW');
    y = this.addBulletList(y, current.highlights, '[+]');
    return y;
  }

  private addPlannedReleases(y: number): number {
    const planned = CHANGELOG.filter((r) => r.status === 'planned');
    if (planned.length === 0) return y;

    y = this.addSectionHeader(y, 'COMING SOON');
    for (const r of planned) {
      y = this.addReleaseCard(y, r, '[ ]');
      y += LINE_GAP_LOOSE;
    }
    return y;
  }

  private addPastReleases(y: number): number {
    const past = CHANGELOG.filter(
      (r) => r.status === 'shipped' && r.version !== APP_VERSION
    );
    if (past.length === 0) return y;

    y = this.addSectionHeader(y, 'PAST VERSIONS');
    for (const r of past) {
      y = this.addReleaseCard(y, r, '[+]');
      y += LINE_GAP_LOOSE;
    }
    return y;
  }

  private addSignOff(y: number): number {
    const cx = DESIGN_WIDTH / 2;

    const rule = this.add
      .text(cx, y, '─'.repeat(38), {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.textMuted,
      })
      .setOrigin(0.5, 0);
    this.content.add(rule);
    y += rule.height + LINE_GAP_TIGHT;

    const signoff = this.add
      .text(cx, y, '// END OF TRANSMISSION', {
        fontFamily: FONT_MONO,
        fontSize: fs(13),
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0);
    this.content.add(signoff);
    return y + signoff.height;
  }

  /**
   * Legal footer. Always shows the support email + copyright notice.
   * Privacy / Terms links only appear once `LEGAL_PAGES_LIVE` is flipped
   * to true in `src/config/branding.ts` — before then the canonical
   * URLs (yeenon.com/privacy, yeenon.com/terms) don't exist yet and
   * we'd be sending beta testers into 404s.
   *
   * Apple App Submission requires both URLs to load — the flag MUST
   * be true before iOS build review.
   */
  private addLegalFooter(y: number): number {
    const cx = DESIGN_WIDTH / 2;

    // Community links — Discord (live chat) + Reddit (async board), both
    // cyan + tappable, sit above the support email so testers can hop
    // into the community channels before reaching for the mail client.
    const discord = this.add
      .text(cx, y, 'JOIN OUR DISCORD', {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true });
    discord.on('pointerup', () => {
      openExternal(DISCORD_URL);
    });
    this.content.add(discord);
    y += discord.height + LINE_GAP_LOOSE;

    const reddit = this.add
      .text(cx, y, 'r/BattleSharkOnline', {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true });
    reddit.on('pointerup', () => {
      openExternal(REDDIT_URL);
    });
    this.content.add(reddit);
    y += reddit.height + LINE_GAP_LOOSE;

    // Support email — cyan + tappable, opens the user's mail client.
    const support = this.add
      .text(cx, y, SUPPORT_EMAIL, {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true });
    support.on('pointerup', () => {
      openExternal(`mailto:${SUPPORT_EMAIL}`);
    });
    this.content.add(support);
    y += support.height + LINE_GAP_LOOSE;

    // Privacy · Terms row — two separate text objects so each carries
    // its own hit target. Centered as a group with a muted "·"
    // separator between them.
    if (LEGAL_PAGES_LIVE) {
      const SEP_GAP = 12;
      const privacy = this.add
        .text(0, y, 'PRIVACY', {
          fontFamily: FONT_MONO,
          fontSize: fs(12),
          color: COLORS.textSecondary,
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      const dot = this.add
        .text(0, y, '·', {
          fontFamily: FONT_MONO,
          fontSize: fs(12),
          color: COLORS.textMuted,
        })
        .setOrigin(0, 0);
      const terms = this.add
        .text(0, y, 'TERMS', {
          fontFamily: FONT_MONO,
          fontSize: fs(12),
          color: COLORS.textSecondary,
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });

      const totalWidth =
        privacy.width + SEP_GAP + dot.width + SEP_GAP + terms.width;
      let xCursor = cx - totalWidth / 2;
      privacy.x = xCursor;
      xCursor += privacy.width + SEP_GAP;
      dot.x = xCursor;
      xCursor += dot.width + SEP_GAP;
      terms.x = xCursor;

      privacy.on('pointerup', () => openExternal(PRIVACY_URL));
      terms.on('pointerup', () => openExternal(TERMS_URL));

      this.content.add(privacy);
      this.content.add(dot);
      this.content.add(terms);
      y += privacy.height + LINE_GAP_LOOSE;
    }

    // Copyright — last line of the page, muted.
    const copyright = this.add
      .text(cx, y, COPYRIGHT_NOTICE, {
        fontFamily: FONT_MONO,
        fontSize: fs(11),
        color: COLORS.textMuted,
      })
      .setLetterSpacing(1)
      .setOrigin(0.5, 0);
    this.content.add(copyright);
    return y + copyright.height;
  }

  // --- Section helpers ---

  private addSectionHeader(y: number, title: string): number {
    const header = this.add
      .text(SIDE_PADDING, y, `> ${title}`, {
        fontFamily: FONT_MONO,
        fontSize: fs(17),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0);
    this.content.add(header);
    y += header.height + 4;

    const rule = this.add
      .text(SIDE_PADDING, y, '─'.repeat(34), {
        fontFamily: FONT_MONO,
        fontSize: fs(13),
        color: intToHex(COLORS.borderSubtleInt),
      })
      .setOrigin(0, 0);
    this.content.add(rule);
    return y + rule.height + LINE_GAP_LOOSE;
  }

  /**
   * One release entry as a card. Header line carries version + codename
   * + (date if shipped); body is a bulleted list of highlights.
   */
  private addReleaseCard(y: number, r: Release, marker: string): number {
    const head = this.add
      .text(SIDE_PADDING, y, `v${r.version} · ${r.codename.toUpperCase()}`, {
        fontFamily: FONT_MONO,
        fontSize: fs(15),
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0);
    this.content.add(head);
    y += head.height + LINE_GAP_TIGHT;

    return this.addBulletList(y, r.highlights, marker, SIDE_PADDING + 16);
  }

  private addBulletList(
    y: number,
    items: readonly string[],
    marker: string,
    indent: number = SIDE_PADDING
  ): number {
    const wrapWidth = DESIGN_WIDTH - SIDE_PADDING - indent;
    for (const item of items) {
      const line = this.add
        .text(indent, y, `${marker} ${item}`, {
          fontFamily: FONT_MONO,
          fontSize: fs(14),
          color: COLORS.textPrimary,
          wordWrap: { width: wrapWidth },
        })
        .setLineSpacing(2)
        .setOrigin(0, 0);
      this.content.add(line);
      y += line.height + LINE_GAP_TIGHT;
    }
    return y;
  }

  private addCenteredLabel(y: number, text: string, colorHex: string): number {
    const label = this.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_MONO,
        fontSize: fs(12),
        color: colorHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5, 0);
    this.content.add(label);
    return y + label.height;
  }

  // --- Scroll input ---

  private applyScrollMask(): void {
    // Rectangular mask covers the area BELOW the header band and
    // ABOVE the bottom nav strip — scrolled content never bleeds
    // under either chrome layer.
    const maskBottom = bottomNavTopY();
    const shape = this.make.graphics({});
    shape.fillStyle(0xffffff, 1);
    shape.fillRect(
      0,
      this.contentTop,
      DESIGN_WIDTH,
      maskBottom - this.contentTop
    );
    const mask = shape.createGeometryMask();
    this.content.setMask(mask);
  }

  private installScrollInput(): void {
    this.input.on(
      'pointerdown',
      (p: Phaser.Input.Pointer) => {
        // Don't initiate drag from inside the page header (logo +
        // Back overlay) or the bottom nav strip.
        if (p.y < this.contentTop) return;
        if (p.y > bottomNavTopY()) return;
        this.dragStart = { pointerY: p.y, contentY: this.content.y };
      }
    );
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown || !this.dragStart) return;
      const dy = p.y - this.dragStart.pointerY;
      this.content.y = this.clampScroll(this.dragStart.contentY + dy);
    });
    this.input.on('pointerup', () => {
      this.dragStart = null;
    });
    this.input.on('pointerupoutside', () => {
      this.dragStart = null;
    });

    // Mouse wheel for desktop — ignored on touch devices.
    this.input.on(
      'wheel',
      (
        _p: Phaser.Input.Pointer,
        _go: Phaser.GameObjects.GameObject[],
        _dx: number,
        deltaY: number
      ) => {
        this.content.y = this.clampScroll(this.content.y - deltaY);
      }
    );
  }

  /**
   * Clamp the content's y-translation so the user can't pull the page
   * past either edge. Top bound: content top sits at CONTENT_TOP (y=0
   * shift). Bottom bound: pulled up just enough to expose the last
   * line at the bottom inset.
   */
  private clampScroll(rawY: number): number {
    // Viewport now ends at the AppNav strip rather than the canvas
    // bottom — content scrolled past it would hide behind the nav.
    const viewportBottom = bottomNavTopY() - VIEWPORT_BOTTOM_INSET;
    const minY = Math.min(this.contentTop, viewportBottom - this.contentHeight);
    const maxY = this.contentTop;
    if (rawY > maxY) return maxY;
    if (rawY < minY) return minY;
    return rawY;
  }
}

function intToHex(int: number): string {
  return `#${int.toString(16).padStart(6, '0')}`;
}
